.root {
  align-items: flex-start;
  background: var(--color-background);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  font: inherit;
  gap: 10px;
  overflow: hidden;
  padding: 10px 12px;
  position: relative;
  text-align: left;
  transition: border-color 120ms ease, background 120ms ease;
  width: 100%;

  &::before {
    align-self: stretch;
    border-radius: 2px;
    content: '';
    flex-shrink: 0;
    margin: -10px 8px -10px -12px;
    width: 3px;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--color-primary);
    outline: none;
  }
}

// Purple accent rail for AI-recommended / preselected cards (pen `gJJV0`).
// Cards without provenance or from manual catalog browse get no rail.
.root-is-ai-picked::before {
  background: #8b5cf6;
}

.root-is-selected {
  background: var(--color-surface-subtle, var(--color-surface));
  border-color: #8b5cf6;
}

.thumb {
  align-items: center;
  background: var(--color-muted);
  border-radius: 8px;
  color: var(--color-muted-fg, var(--color-muted));
  display: flex;
  flex-shrink: 0;
  height: 64px;
  justify-content: center;
  // <img> shares this class with the placeholder span. object-fit: cover
  // crops oversized photos, object-position: center keeps the focal point.
  object-fit: cover;
  object-position: center;
  width: 64px;
}

.icon-frame {
  align-items: center;
  background: var(--color-primary);
  border-radius: 8px;
  color: var(--color-primary-foreground, var(--color-background));
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.icon-frame-tone-muted {
  background: var(--color-muted);
  color: var(--color-foreground);
}

.title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 4px;
  min-width: 0;
}

.stars {
  align-items: center;
  display: flex;
  gap: 2px;
}

.star {
  color: var(--color-border);
}

.star-filled {
  color: var(--color-warning, var(--color-primary));
  fill: currentColor;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.matched-chip {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 10px;
  color: #065f46;
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
  padding: 2px 6px;
}

// Fit % badge (pen `JUbT2` green, `UvO4b` amber). Compact pill placed in
// the title row to the right of "Best match" / provenance badge.
.fit-badge {
  border-radius: 100px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 2px 7px;
}

.fit-badge-is-strong {
  background: #dcfce7;
  color: #166534;
}

.fit-badge-is-weak {
  background: #fef3c7;
  color: #92400e;
}

// Pen `ac-c1` reason banner — light-purple-tint pill under the chips
// carrying the LLM's "why this matches" sentence.
.reason {
  background: #f5f3ff;
  border-radius: 6px;
  color: #5b21b6;
  display: block;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  padding: 6px 8px;
}

// Provenance tag: "AI-picked" (pen `gJJV0`) or "From catalog" (pen `Z7zVMd`).
.provenance-tag {
  font-size: 11px;
  font-weight: 500;
  line-height: 1;

  &.provenance-tag-is-ai {
    background: #f3e8ff;
    border-radius: 100px;
    color: #7c3aed;
    padding: 2px 7px;
  }

  &.provenance-tag-is-catalog {
    color: var(--color-muted-fg, var(--color-muted));
    font-style: italic;
  }
}

// Pen `oj1Qr` (unchecked) / `Asjsd` (checked) — 20×20, cornerRadius 4,
// white fill with #CBD5E1 stroke when empty; #8B5CF6 fill with white
// glyph when picked.
.checkbox {
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 4px;
  color: #ffffff;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  margin-top: 2px;
  width: 20px;
}

.checkbox-is-checked {
  background: #8b5cf6;
  border-color: #8b5cf6;
}
